<template>
    <!-- Main Panel (all states except multi-panel AUDIT mode) -->
    <SidePanel
        :visible="
            !!(auditStore && auditStore?.isPanelVisible) && !useMultiPanelMode
        "
        :title="auditStore?.panelTitle"
        size="panel-md"
        :confirm-close="performingAudit"
        @close="closePanel"
    >
        <template class="d-flex flex-column h-100">
            <AuditSummary
                ref="auditSummary"
                v-if="isSummaryPanel(auditStore?.panelType)"
                :panel-type="auditStore?.panelType"
                :audit="auditStore?.audit"
                :user-options="userOptions"
                @open-modal="openModal(auditStore?.audit)"
                @areas="openOptionPanel('Area', 'audit-area')"
                @edit-audit="
                    changePanel(
                        auditStore?.audit.completed_at
                            ? 'editSummary'
                            : 'schedule',
                        auditStore?.audit
                    )
                "
                @view-audit="changePanel('viewTemplate', auditStore?.audit)"
                @perform-audit="
                    changePanel('performTemplate', auditStore?.audit)
                "
                @submit="handleAuditForm"
            />
            <AuditTemplateContent
                ref="auditTemplateContent"
                v-else-if="isTemplatePanel(auditStore?.panelType)"
                :audit="auditStore?.audit"
                :can-edit="auditStore?.panelType !== 'viewTemplate'"
                :user-options="userOptions"
                @areas="openOptionPanel('Area', 'audit-area')"
                @started="handleMainPanelStarted"
                @submit="handleAuditSubmit"
            />
            <ScheduleAuditForm
                v-else-if="auditStore?.panelType == 'schedule'"
                :audit="auditStore?.audit || {}"
                :user-options="userOptions"
                @areas="openOptionPanel('Area', 'audit-area')"
                @submit="handleAuditSubmit"
            />
            <OptionPanel
                :visible="isOptionPanelVisible"
                :option-title="optionTitle"
                :endpoint="endpoint"
                @submit="handleOptionFormSubmit"
                @close="closeOptionPanel"
            />
        </template>
        <template #footer>
            <div
                v-if="showBackButton || showSubmitButton || showEditButton"
                class="d-flex gap-2 pt-4 border-top mt-4"
            >
                <!-- Back Button -->
                <button
                    v-if="showBackButton"
                    type="button"
                    class="btn btn-white"
                    @click="handleBack"
                >
                    <div class="d-flex gap-2">
                        <i class="fi fi-chevron-left"></i>
                        Back
                    </div>
                </button>
                <!-- Submit Button -->
                <button
                    v-if="showSubmitButton"
                    type="submit"
                    class="btn btn-secondary flex-grow-1"
                    @click="submitToComponent"
                >
                    Submit
                </button>
                <!-- Edit Button -->
                <button
                    v-if="showEditButton"
                    class="icon-btn icon-btn-light ms-auto"
                    @click="handleEditAuditTemplate"
                >
                    <i class="fi fi-edit"></i>
                </button>
            </div>
        </template>
    </SidePanel>

    <!-- PerformTemplate Panels Container -->
    <div
        ref="panelsContainer"
        class="panels-container"
        :class="{
            'has-visible-panels': useMultiPanelMode,
        }"
        @scroll="updateScrollState"
    >
        <TransitionGroup
            name="panel-slide"
            tag="div"
            class="panels-transition-group"
        >
            <div
                v-for="(panel, index) in panelInstances"
                :key="panel.id"
                class="panel-wrapper"
            >
                <SidePanel
                    :visible="
                        useMultiPanelMode &&
                        (index === 0
                            ? !!(auditStore && auditStore?.isPanelVisible)
                            : true)
                    "
                    :title="auditStore?.panelTitle"
                    size="panel-md"
                    :confirm-close="performingAudit"
                    :panel-style="getPanelStyle(index)"
                    @close="(source) => closePanelInstance(panel.id, source)"
                >
                    <template class="d-flex flex-column h-100">
                        <AuditTemplateContent
                            :ref="getPanelRef(panel.id)"
                            :audit="panel.auditData || null"
                            :can-edit="true"
                            :user-options="userOptions"
                            :panel-id="panel.id"
                            @areas="openOptionPanel('Area', 'audit-area')"
                            @started="
                                (auditType) =>
                                    handlePanelStarted(panel.id, auditType)
                            "
                            @submit="handlePanelSubmit(panel.id)"
                        />
                    </template>
                    <template #footer>
                        <div
                            v-if="performingAudit"
                            class="d-flex gap-2 pt-4 border-top mt-4"
                        >
                            <!-- Submit Button -->
                            <button
                                type="submit"
                                class="btn btn-secondary w-100"
                                @click="submitPanelInstance(panel.id)"
                            >
                                Submit
                            </button>
                        </div>
                    </template>
                </SidePanel>
            </div>
        </TransitionGroup>
    </div>

    <!-- Fixed Footer Banner for multi-panel AUDIT mode -->
    <div v-if="useMultiPanelMode" class="fixed-footer-banner">
        <div class="footer-banner-content">
            <!-- Back Button -->
            <div class="footer-banner-left">
                <button
                    v-if="showBackInFooter"
                    type="button"
                    class="btn btn-white"
                    @click="handleBackForFirstPanel"
                >
                    <div class="d-flex gap-2">
                        <i v-if="!$isMobile" class="fi fi-chevron-left"></i>
                        Back<span v-if="!$isMobile"> to Selection</span>
                    </div>
                </button>
            </div>
            <!-- Scroll Navigation -->
            <div
                v-if="canScrollLeft || canScrollRight"
                class="footer-banner-center"
            >
                <button
                    type="button"
                    class="icon-btn icon-btn-lg icon-btn-white"
                    :disabled="!canScrollLeft"
                    @click="scrollLeft"
                    title="Scroll left"
                >
                    <i class="fi fi-chevron-left"></i>
                </button>
                <button
                    type="button"
                    class="icon-btn icon-btn-lg icon-btn-white"
                    :disabled="!canScrollRight"
                    @click="scrollRight"
                    title="Scroll right"
                >
                    <i class="fi fi-chevron-right"></i>
                </button>
            </div>
            <!-- Add Another Button -->
            <div class="footer-banner-right">
                <button class="btn btn-white" @click="submitAllPanels">
                    Submit All
                </button>
                <button
                    type="button"
                    class="btn btn-light"
                    :class="$isMobile ? 'icon-btn icon-btn-lg' : ''"
                    :disabled="!canAddAnotherPanel"
                    @click="addAnotherPanel"
                >
                    <div class="d-flex gap-2">
                        <span v-if="!$isMobile">Add another audit</span>
                        <i class="fi fi-add"></i>
                    </div>
                </button>
            </div>
        </div>
    </div>

    <ConfirmModal
        :visible="isModalVisible"
        title="Delete Audit?"
        confirm-button-text="Delete Audit"
        @close="closeModal"
        @confirm="handleDeleteAudit(auditStore?.audit)"
    >
        Are you sure you want to delete this audit?
    </ConfirmModal>
</template>

<script>
import ConfirmModal from '../ConfirmModal.vue';
import SidePanel from '../SidePanel.vue';
import AuditSummary from './AuditSummary.vue';
import AuditTemplateContent from './AuditTemplateContent.vue';
import ScheduleAuditForm from './ScheduleAuditForm.vue';
import OptionPanel from './OptionPanel.vue';

import { useAuditStore } from '@/stores/audits';
import { saveAudit, deleteAudit } from '@/utils/auditOperations';
import { getUserOptions } from '@/utils/userOptions';
import {
    isSummaryPanel,
    isTemplatePanel,
    getBackPanelType,
} from '@/utils/auditPanelStrategies';

export default {
    name: 'AuditsPanel',
    components: {
        SidePanel,
        AuditSummary,
        AuditTemplateContent,
        OptionPanel,
        ScheduleAuditForm,
        ConfirmModal,
    },
    props: {
        visible: {
            type: Boolean,
            required: false,
            default: false,
        },
    },
    data() {
        return {
            auditStore: null,
            performingAudit: false,
            isModalVisible: false,
            userOptions: [],
            // Option panel state
            isOptionPanelVisible: false,
            optionTitle: '',
            endpoint: '',
            // All performTemplate panels tracked in array (including first panel)
            // Always keep first panel instance rendered so transitions work properly
            // Additional panels (index > 0) are always performing audits
            panelInstances: [{ id: 1, auditData: null }],
            nextPanelId: 2,
            // Store the audit template data to clone for new panels
            sharedAuditData: null,
            // Scroll state
            canScrollLeft: false,
            canScrollRight: false,
        };
    },
    computed: {
        isAdminRoute() {
            return this.$route.matched.some((r) => r.name === 'Admin');
        },
        // Multi-panel mode: only when performTemplate + AUDIT type + performing
        useMultiPanelMode() {
            return (
                this.auditStore?.panelType === 'performTemplate' &&
                this.performingAudit &&
                this.sharedAuditData?.audit_type === 'AUDIT'
            );
        },
        // Scheduled audits can open performTemplate directly in form mode
        // (without emitting started), so treat this as main-panel performing state.
        isScheduledPerformMainPanel() {
            return (
                this.auditStore?.panelType === 'performTemplate' &&
                !!this.auditStore?.audit?.template_id &&
                !this.auditStore?.audit?.form_json &&
                !this.performingAudit
            );
        },
        // Non-AUDIT performTemplate in performing state (uses main panel with footer buttons)
        isNonAuditPerforming() {
            return (
                this.auditStore?.panelType === 'performTemplate' &&
                this.performingAudit &&
                !this.useMultiPanelMode
            );
        },
        // Footer button visibility computed properties
        showBackButton() {
            if (this.auditStore?.panelType === 'uploadSummary') {
                return false;
            }
            return (
                this.isNonAuditPerforming ||
                this.auditStore?.panelType === 'viewTemplate' ||
                this.auditStore?.panelType === 'editTemplate' ||
                this.auditStore?.panelType === 'editSummary'
            );
        },
        showSubmitButton() {
            return (
                this.isScheduledPerformMainPanel ||
                this.isNonAuditPerforming ||
                this.auditStore?.panelType === 'editTemplate' ||
                this.auditStore?.panelType === 'editSummary' ||
                this.auditStore?.panelType === 'uploadSummary'
            );
        },
        showEditButton() {
            return (
                this.auditStore?.panelType === 'viewTemplate' &&
                this.$userStore.hasPerm('audits:write') &&
                (!this.auditStore.audit.app_admin_only || this.isAdminRoute)
            );
        },
        // Check if we can add another panel (max 3 total)
        canAddAnotherPanel() {
            return (
                this.panelInstances.length < 4 &&
                this.sharedAuditData?.audit_type === 'AUDIT'
            );
        },
        // Check if any panel is performing an audit
        hasPerformingAudit() {
            return this.performingAudit;
        },
        // Show back button in footer if performing audit
        showBackInFooter() {
            return this.performingAudit;
        },
    },
    methods: {
        // Expose strategy functions to template
        isSummaryPanel,
        isTemplatePanel,
        getBackPanelType,

        changePanel(type, audit) {
            this.auditStore.openPanel(type, audit);
            if (type == 'performTemplate') {
                // Reset first panel when starting performTemplate
                if (this.panelInstances.length > 0) {
                    this.panelInstances[0] = {
                        id: this.panelInstances[0].id, // Keep same ID
                        auditData: audit || null,
                    };
                }
                // Remove any additional panels
                if (this.panelInstances.length > 1) {
                    this.panelInstances.splice(1);
                }
                // Reset shared audit data and performing state when starting fresh
                this.sharedAuditData = null;
                this.performingAudit = false;
            } else {
                // Reset panels when switching away from performTemplate
                if (this.panelInstances.length > 0) {
                    this.panelInstances[0].auditData = null;
                }
                // Remove any additional panels
                if (this.panelInstances.length > 1) {
                    this.panelInstances.splice(1);
                }
                this.performingAudit = false;
                this.sharedAuditData = null;
            }
        },
        handleMainPanelStarted(auditType) {
            // Handle started event from main panel AuditTemplateContent
            // Capture full audit data from the main panel component
            const component = this.$refs.auditTemplateContent;
            if (component?.localAudit) {
                this.sharedAuditData = {
                    template_id: component.localAudit.template_id,
                    audit_type: component.localAudit.audit_type,
                    date: component.localAudit.date,
                    auditor_id: component.localAudit.auditor_id,
                    site_id: component.localAudit.site_id,
                    area_id: component.localAudit.area_id,
                    name: component.localAudit.name,
                    description: component.localAudit.description,
                    company_id: component.localAudit.company_id,
                    form_max_score: component.localAudit.form_max_score,
                };
                // If AUDIT type, pass data to first panel instance so it doesn't restart
                if (auditType === 'AUDIT' && this.panelInstances.length > 0) {
                    this.panelInstances[0].auditData = {
                        ...this.sharedAuditData,
                    };
                }
            } else {
                this.sharedAuditData = { audit_type: auditType };
            }
            this.performingAudit = true;
        },
        handlePanelStarted(panelId, auditType) {
            // Set performing state and audit type
            this.performingAudit = true;
            if (auditType) {
                this.sharedAuditData = {
                    ...this.sharedAuditData,
                    audit_type: auditType,
                };
            }
            // Capture audit data from the rightmost panel (last in array)
            const index = this.panelInstances.findIndex(
                (p) => p.id === panelId
            );
            if (index === this.panelInstances.length - 1) {
                this.capturePanelAuditData(panelId);
            }
        },
        capturePanelAuditData(panelId) {
            // Capture audit data from a panel instance
            const refKey = this.getPanelRef(panelId);
            const refs = this.$refs[refKey];
            if (refs) {
                const component = Array.isArray(refs) ? refs[0] : refs;
                if (
                    component &&
                    component.localAudit &&
                    component.panelType !== 'select'
                ) {
                    // Capture the audit metadata (template, date, auditor, site, area)
                    const auditData = {
                        template_id: component.localAudit.template_id,
                        audit_type: component.localAudit.audit_type,
                        date: component.localAudit.date,
                        auditor_id: component.localAudit.auditor_id,
                        site_id: component.localAudit.site_id,
                        area_id: component.localAudit.area_id,
                        name: component.localAudit.name,
                        description: component.localAudit.description,
                        company_id: component.localAudit.company_id,
                        form_max_score: component.localAudit.form_max_score,
                    };

                    // Store in shared data for cloning to new panels
                    this.sharedAuditData = auditData;
                }
            }
        },
        openOptionPanel(type, endpoint) {
            this.optionTitle = type;
            this.endpoint = endpoint;
            this.isOptionPanelVisible = true;
        },
        closeOptionPanel() {
            this.isOptionPanelVisible = false;
            this.optionTitle = '';
            this.endpoint = '';
        },
        closePanel() {
            this.auditStore.closePanel();
            this.performingAudit = false;
            // Reset first panel but keep it rendered
            if (this.panelInstances.length > 0) {
                this.panelInstances[0].auditData = null;
            }
            // Remove any additional panels
            if (this.panelInstances.length > 1) {
                this.panelInstances.splice(1);
            }
            this.sharedAuditData = null;
        },
        openModal() {
            this.isModalVisible = true;
        },
        closeModal() {
            this.isModalVisible = false;
        },
        submitToComponent() {
            if (this.isScheduledPerformMainPanel || this.isNonAuditPerforming) {
                // performTemplate in main panel mode - submit via main panel ref
                if (this.$refs.auditTemplateContent?.saveAudit) {
                    this.$refs.auditTemplateContent.saveAudit();
                }
            } else if (this.auditStore?.panelType === 'editTemplate') {
                this.submitCreateAudit();
            } else {
                this.submitAuditForm();
            }
        },
        submitAllPanels() {
            this.panelInstances.forEach((panel) => {
                this.submitPanelInstance(panel.id);
            });
        },
        submitCreateAudit() {
            // Trigger save on child component via ref (only for editTemplate)
            if (this.$refs.auditTemplateContent) {
                this.$refs.auditTemplateContent.saveAudit();
            }
        },
        handleBack() {
            if (this.isNonAuditPerforming) {
                // Non-AUDIT performTemplate - go back to selection
                if (this.$refs.auditTemplateContent?.backToSelection) {
                    this.$refs.auditTemplateContent.backToSelection();
                }
                this.performingAudit = false;
                this.sharedAuditData = null;
            } else {
                const backType = getBackPanelType(this.auditStore?.panelType);
                if (backType !== null) {
                    this.auditStore.panelType = backType;
                }
            }
        },
        submitAuditForm() {
            // Trigger submit on child component via ref
            if (this.$refs.auditSummary) {
                this.$refs.auditSummary.submitForm();
            }
        },
        async getUserList() {
            this.userOptions = await getUserOptions(this);
        },
        handleOptionFormSubmit() {
            this.closeOptionPanel();
        },
        handleApiError(areaResponse, siteResponse) {
            this.$alertStore.pushError(
                areaResponse?.message || siteResponse?.message
            );
        },
        handleAuditForm(audit, newEntry, sendNotification = false) {
            // Use single audit endpoint for all audit types
            const action = newEntry ? 'create' : 'update';
            const endpoint = `audit/${action}`;

            this.$postData(endpoint, audit).then((response) => {
                if (response?.success) {
                    const auditId = response.data?.id || audit?.id;

                    // Send notification if checkbox was checked (only for new entries)
                    if (sendNotification && newEntry && auditId) {
                        this.$postData('audit/notify-upload', {
                            audit_id: auditId,
                        })
                            .then((notifyResponse) => {
                                if (!notifyResponse?.success) {
                                    this.$alertStore.pushError(
                                        notifyResponse?.message ||
                                            'Failed to send notification'
                                    );
                                }
                            })
                            .catch(() => {
                                this.$alertStore.pushError(
                                    'Failed to send notification'
                                );
                            });
                    }

                    this.handleAuditSubmit();
                } else {
                    this.$alertStore.pushError(response?.message);
                }
            });
        },
        handleAuditSubmit() {
            this.auditStore.incrementAuditHistoryVersionKey();
            this.auditStore.incrementAuditScheduleVersionKey();
            this.closePanel();
        },
        handleDeleteAudit(audit) {
            deleteAudit(this, audit).then((response) => {
                if (response?.success) {
                    this.handleAuditSubmit();
                    this.isModalVisible = false;
                } else {
                    this.$alertStore.pushError(response?.message);
                }
            });
        },
        handleEditAuditTemplate() {
            if (!this.$userStore.companyHasPerm('audits:write')) {
                this.$userStore.togglePremiumModal(true);
                return;
            }
            this.auditStore.panelType = 'editTemplate';
        },
        // Multiple panel methods
        addAnotherPanel() {
            // Always capture the latest audit data from the rightmost panel (last in array)
            const rightmostPanel =
                this.panelInstances[this.panelInstances.length - 1];
            if (rightmostPanel) {
                this.capturePanelAuditData(rightmostPanel.id);
            }

            // Create new panel with cloned audit data
            // Additional panels (index > 0) are always performing audits
            const newPanel = {
                id: this.nextPanelId++,
                auditData: this.sharedAuditData
                    ? { ...this.sharedAuditData }
                    : null,
            };
            this.panelInstances.push(newPanel);
            // Update scroll state after adding panel
            this.$nextTick(() => {
                this.updateScrollState();
            });
        },
        closePanelInstance(panelId, closeSource) {
            // If in performTemplate mode and performing audit, and close is from overlay,
            // close all panels when confirming
            if (
                this.useMultiPanelMode &&
                this.performingAudit &&
                closeSource === 'overlay'
            ) {
                this.closeAllPanelsAndReset();
                this.$nextTick(() => {
                    this.updateScrollState();
                });
                return;
            }

            const index = this.panelInstances.findIndex(
                (p) => p.id === panelId
            );
            if (index !== -1) {
                if (this.panelInstances.length === 1) {
                    this.closePanel();
                } else {
                    // Additional panel - remove it
                    this.panelInstances.splice(index, 1);
                    // Update scroll state after panel removal
                    this.$nextTick(() => {
                        this.updateScrollState();
                    });
                }
            }
        },
        closeAllPanelsAndReset() {
            // Close all panels and reset performTemplate state
            this.auditStore.closePanel();
            this.performingAudit = false;
            // Reset all panel instances
            this.panelInstances = [{ id: 1, auditData: null }];
            this.nextPanelId = 2;
            this.sharedAuditData = null;
        },
        getPanelRef(panelId) {
            return `auditTemplateContent_${panelId}`;
        },
        submitPanelInstance(panelId) {
            const refKey = this.getPanelRef(panelId);
            const refs = this.$refs[refKey];
            if (refs) {
                const component = Array.isArray(refs) ? refs[0] : refs;
                if (component && component.saveAudit) {
                    component.saveAudit();
                }
            }
        },
        handlePanelSubmit(panelId) {
            // Handle submit for any panel in performTemplate mode
            this.auditStore.incrementAuditHistoryVersionKey();
            this.auditStore.incrementAuditScheduleVersionKey();
            // Close the specific panel that was submitted
            // Remaining panels will automatically shift left due to array re-render
            this.closePanelInstance(panelId);
            // Update scroll state after submit (closePanelInstance will also update, but this ensures it)
            this.$nextTick(() => {
                this.updateScrollState();
            });
        },
        isLastPanel(index) {
            // Check if this panel is the rightmost (last in array)
            return index === this.panelInstances.length - 1;
        },
        handleBackForFirstPanel() {
            // Return to selection: reset first panel and remove all additional panels
            if (this.panelInstances.length > 0) {
                const refKey = this.getPanelRef(this.panelInstances[0].id);
                const refs = this.$refs[refKey];
                if (refs) {
                    const component = Array.isArray(refs) ? refs[0] : refs;
                    if (component && component.backToSelection) {
                        component.backToSelection();
                    }
                }
                // Remove all additional panels
                if (this.panelInstances.length > 1) {
                    this.panelInstances.splice(1);
                }
                // Reset performing state
                this.performingAudit = false;
            }
        },
        getPanelStyle(index) {
            // Set z-index for proper stacking
            return {
                zIndex: 1050 + index,
            };
        },
        updateScrollState() {
            const container = this.$refs.panelsContainer;

            // Early returns for invalid states
            if (!container) {
                this.canScrollLeft = false;
                this.canScrollRight = false;
                return;
            }

            // Only update scroll state when in performTemplate mode
            if (!this.useMultiPanelMode) {
                this.canScrollLeft = false;
                this.canScrollRight = false;
                return;
            }

            // Need at least 2 panels to scroll
            if (this.panelInstances.length < 2) {
                this.canScrollLeft = false;
                this.canScrollRight = false;
                return;
            }

            const { scrollLeft, scrollWidth, clientWidth } = container;

            // Validate dimensions - if content hasn't rendered yet, wait
            if (!scrollWidth || !clientWidth || scrollWidth <= clientWidth) {
                this.canScrollLeft = false;
                this.canScrollRight = false;
                return;
            }

            const maxScroll = Math.max(0, scrollWidth - clientWidth);
            const tolerance = 2;

            // Check if we can scroll in either direction
            // Account for small rounding and subpixel rendering differences
            this.canScrollLeft = scrollLeft > tolerance;
            this.canScrollRight = scrollLeft < maxScroll - tolerance;
        },
        scrollLeft() {
            const container = this.$refs.panelsContainer;
            if (!container) return;
            // Get the width of one panel wrapper to scroll by
            const transitionGroup = container.querySelector(
                '.panels-transition-group'
            );
            if (!transitionGroup) return;
            const firstPanel = transitionGroup.querySelector('.panel-wrapper');
            if (!firstPanel) return;
            const scrollAmount =
                firstPanel.offsetWidth +
                    parseFloat(getComputedStyle(transitionGroup).gap) || 525;
            container.scrollBy({
                left: -scrollAmount,
                behavior: 'smooth',
            });
        },
        scrollRight() {
            const container = this.$refs.panelsContainer;
            if (!container) return;
            // Get the width of one panel wrapper to scroll by
            const transitionGroup = container.querySelector(
                '.panels-transition-group'
            );
            if (!transitionGroup) return;
            const firstPanel = transitionGroup.querySelector('.panel-wrapper');
            if (!firstPanel) return;
            const scrollAmount =
                firstPanel.offsetWidth +
                    parseFloat(getComputedStyle(transitionGroup).gap) || 525;
            container.scrollBy({
                left: scrollAmount,
                behavior: 'smooth',
            });
        },
    },
    watch: {
        // Watch panel instances to update scroll state when panels change
        panelInstances: {
            handler() {
                this.$nextTick(() => {
                    this.updateScrollState();
                });
            },
            deep: true,
        },
        // Watch for panelType changes to reset performTemplate panels
        'auditStore.panelType'(newType) {
            if (newType === 'performTemplate') {
                // Reset first panel when switching to performTemplate
                if (this.panelInstances.length > 0) {
                    this.panelInstances[0].auditData = null;
                }
                // Remove any additional panels
                if (this.panelInstances.length > 1) {
                    this.panelInstances.splice(1);
                }
                this.sharedAuditData = null;
                this.performingAudit = false;
                this.$nextTick(() => {
                    this.updateScrollState();
                });
            } else if (newType !== 'performTemplate') {
                // Reset panels when switching away from performTemplate
                if (this.panelInstances.length > 0) {
                    this.panelInstances[0].auditData = null;
                }
                // Remove any additional panels
                if (this.panelInstances.length > 1) {
                    this.panelInstances.splice(1);
                }
                this.performingAudit = false;
                this.sharedAuditData = null;
            }
        },
    },
    created() {
        this.auditStore = useAuditStore();
    },
    mounted() {
        this.getUserList();
        // Update scroll state on mount and window resize
        this.$nextTick(() => {
            this.updateScrollState();
        });
        window.addEventListener('resize', this.updateScrollState);
    },
    beforeUnmount() {
        window.removeEventListener('resize', this.updateScrollState);
    },
};
</script>

<style scoped>
.fixed-footer-banner {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    background: rgba(45, 45, 45, 0.5);
    backdrop-filter: blur(6px);
    padding: var(--space-2, 0.5rem) var(--space-5, 1.25rem);
    z-index: 1060;
    box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.1);
}

.footer-banner-content {
    display: flex;
    align-items: center;
    justify-content: space-between;
    max-width: 100%;
    margin: 0 auto;
}

.footer-banner-left,
.footer-banner-center,
.footer-banner-right {
    /* flex: 1; */
    display: flex;
    align-items: center;
    gap: var(--space-2, 0.5rem);
    width: fit-content;
    white-space: nowrap;
}

.footer-banner-left {
    justify-content: flex-start;
}

.footer-banner-center {
    justify-content: center;
    gap: var(--space-2, 0.5rem);
}

.footer-banner-center .icon-btn {
    min-width: auto;
    padding: var(--space-2, 0.5rem);
}

.footer-banner-right {
    justify-content: flex-end;
}

.panels-container {
    position: fixed;
    top: 1.25rem;
    right: 0;
    left: 0;
    bottom: 4rem;
    z-index: 1050;
    width: 100%;
    overflow-x: auto;
    overflow-y: hidden;
    scroll-snap-type: x mandatory;
    pointer-events: none; /* Don't block clicks when empty/hidden */
    /* Hide scrollbar */
    scrollbar-width: none; /* Firefox */
    -ms-overflow-style: none; /* IE and Edge */
}

.panels-container::-webkit-scrollbar {
    display: none; /* Chrome, Safari, Opera */
}

.panels-container.has-visible-panels {
    pointer-events: auto; /* Enable clicks when panels are visible */
}

.panels-transition-group {
    display: flex;
    height: 100%;
    flex-direction: row-reverse;
    align-items: stretch;
    width: max-content;
    min-width: 100%;
    flex-shrink: 0;
}

.panel-wrapper {
    flex: 1 1 500px;
    min-width: 320px;
    max-width: calc(500px + 1.25rem);
    width: 100%;
    height: 100%;
    position: relative;
    scroll-snap-align: end;
    flex-shrink: 1;
    padding-right: 0.625rem;
    padding-left: 0.625rem;
}

/* Add margin to first and last panels */
.panel-wrapper:first-child {
    padding-right: 1.25rem;
    max-width: calc(500px + 1.875rem);
}

.panel-wrapper:last-child {
    padding-left: 1.25rem;
    max-width: calc(500px + 1.875rem);
}

.panel-wrapper:first-child:last-child {
    max-width: calc(500px + 2.5rem);
}

.panels-container :deep(.side-panel) {
    position: relative;
    width: 100%;
    height: 100%;
    top: 0;
    right: 0;
    bottom: 0;
    left: auto;
    margin: 0;
}

.panels-container :deep(.side-panel.panel-md) {
    max-width: 100vw !important;
}

/* Panel slide animations */
.panel-slide-enter-active {
    transition: all 0.35s cubic-bezier(0.4, 0, 0.2, 1);
}

.panel-slide-leave-active {
    transition: all 0.3s cubic-bezier(0.4, 0, 1, 1);
}

.panel-slide-enter-from {
    transform: translateX(100%);
    opacity: 0;
}

.panel-slide-leave-to {
    transform: translateX(100%);
    opacity: 0;
}

.panel-slide-move {
    transition: transform 0.3s ease;
}

@media only screen and (max-width: 499.98px) {
    .panels-container {
        top: var(--space-2);
        max-width: 100%;
    }

    .panels-transition-group {
        gap: var(--space-2);
    }

    .panel-wrapper {
        min-width: 0;
        max-width: 100vw;
        flex: 0 1 100%;
        width: 100vw;
        padding-right: 0.5rem !important;
        padding-left: 0.5rem !important;
    }

    .fixed-footer-banner {
        padding: var(--space-2, 0.5rem) var(--space-2, 0.5rem);
    }
}
</style>
